import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import Zone from "@insite/client-framework/Components/Zone";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getdiscrepancyDetails } from "@tKgpco/Services/DiscrepancyService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import { loadDiscrepancyDetailsCollection } from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyDetailsCollection";
import SmallHeadingAndContent from "@tKgpco/Widgets/Common/SmallHeadingAndContent";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    let rmaNumber;

    if (location && location.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, {
            rmaNumber: "",
        });
        rmaNumber = parsedQuery.rmaNumber;
    }

    return {
        userName: state.context.session.userName,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isLoggedIN: state.context.session.isAuthenticated,
        rmaDetails: state.KGPState.RMAStore.RMACollectionDetails,
        location,
        currencySymbol: state.context.session.currency?.currencySymbol,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    loadDiscrepancyCollection,
    loadDiscrepancyDetailsCollection,
};

export interface RMASummaryStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    headerContainer?: GridItemProps;
    productContainer?: InjectableCss;
    imageContainer?: GridItemProps;
    contentContainer?: GridItemProps;
    content?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    unitPriceContent?: TypographyProps;
    footerPoWrapper?: InjectableCss;
    poLineContainer?: GridItemProps;
    poLineNumber?: InjectableCss;
    poLineNumberParent?: InjectableCss;
    trackOrderHeadingAndText?: SmallHeadingAndTextStyles;
    lineNumberParentGrid?: GridItemProps;
    lineNumber?: InjectableCss;
    lineNumberParent?: InjectableCss;
    brandTitle?: TypographyProps;
    productTitle?: TypographyProps;
}

export const RMASummaryStyles: RMASummaryStyles = {
    gridContainer: {
        gap: 10,
        css: css`
            @media print {
                page-break-before: always;
            }
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h3",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-bottom: 10px;
            margin-bottom: 15px !important;
            width: 100%;
            border-bottom: 1px solid #edf4f8;
        `,
    },
    headerContainer: {
        width: 12,
        css: css`
            background-color: #edf4f8;
            padding: 0px;
        `,
    },
    productContainer: {
        css: css`
            border: 1.5px solid #edf4f8;
            width: 100%;
            padding-bottom: 12px;
            margin-bottom: 25px;
        `,
    },
    imageContainer: {
        width: 2,
    },
    contentContainer: {
        width: 10,
        css: css`
            flex-wrap: wrap;
        `,
    },
    content: {
        width: 3,
        css: css`
            flex-direction: column;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h3",
            as: "h3",
            css: css`
                text-transform: uppercase;
                font-size: 14px !important;
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
        text: {
            variant: "h4",
            as: "h4",
            css: css`
                font-weight: 400 !important;
                margin-top: 0 !important;
                @media print {
                    font-size: 12px;
                }
            `,
        },
    },
    unitPriceContent: {
        color: "primary",
    },
    footerPoWrapper: {
        css: css`
            padding-top: 0px;
            padding-bottom: 20px;
            padding-right: 0px !important;
            > div > div {
                align-items: center;
                margin-top: 0px;
                margin-bottom: 0px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 45px;
                    `,
                    css`
                        padding-top: 20px;
                    `,
                    css`
                        padding-top: 20px;
                    `,
                    css`
                        padding: 0px 30px;
                    `,
                    css`
                        padding: 0px 30px;
                    `,
                ])}
        `,
    },
    poLineContainer: {
        css: css`
            padding: 0px 10px;
            > div {
                display: flex;
                flex-direction: row;
                align-items: center;
            }
            @media print {
                width: 100%;
            }
        `,
    },
    poLineNumberParent: {
        css: css`
            border: 1px solid rgb(225, 231, 237);
            background-color: rgb(237, 244, 248);
            border-radius: 8px;
            align-self: flex-start;
            margin-left: 4px;
        `,
    },
    poLineNumber: {
        css: css`
            font-weight: 600;
            padding: 7px;
            min-width: 75px;
            min-height: 40px;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 38px;
                        `,
                        css`
                            min-height: 38px;
                        `,
                        css`
                            min-height: 38px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    trackOrderHeadingAndText: {
        text: {
            size: 13,
            css: css`
                padding: 12px 4px 4px 4px;
                display: flex;
                justify-content: flex-start;
            `,
        },
        heading: {
            css: css`
                font-size: 13px !important;
                text-transform: uppercase;
                padding-left: 0px;
                padding-right: 0px;
            `,
        },
    },
    lineNumberParentGrid: {
        css: css`
            padding: 0px 10px;
            display: flex;
            justify-content: right;
            padding-right: 30px;
            > div {
                margin-right: 0px;
                margin-bottom: 0px;
            }
        `,
    },
    lineNumberParent: {
        css: css`
            bottom: 0;
            margin: 13px 29px;
            margin-top: 0px !important;
            right: 0;
            border: 1px solid rgb(225, 231, 237);
            background-color: rgb(237, 244, 248);
            border-radius: 8px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            float: right;
                            position: inherit;
                        `,
                        css`
                            float: right;
                            position: inherit;
                        `,
                        css`
                            float: right;
                            position: inherit;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    lineNumber: {
        css: css`
            font-weight: 600;
            margin: 7px;
            min-width: 35px;
            text-align: center;
        `,
    },
    brandTitle: {
        variant: "h3",
        as: "h3",
        color: "primary",
        css: css`
            text-transform: uppercase;
            font-size: 14px !important;
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
        `,
    },
    productTitle: {
        variant: "h3",
        as: "h3",
        css: css`
            font-weight: 600 !important;
            margin-top: 0 !important;
            font-size: 14px !important;
            text-transform: uppercase;
            @media print {
                font-size: 12px;
            }
        `,
    },
};

const styles = RMASummaryStyles;

const statusColorMapping: Record<string, string> = {
    New: "#ccccff",
    Open: "#ccccff",
    "In Progress": "#ccccff",
    Pending: "#ccccff",
    Complete: "#ccccff",
};

const OrderStatusDisplay = styled.span<{ status: string }>`
    padding: 15px 8px;
    color: #000;
    font-weight: bold;
    font-size: 14px;
    min-width: 135px;
    background-color: ${({ status }) =>
        statusColorMapping[status] ? statusColorMapping[status] : getColor("primary")};
`;

const RMASummary: React.FC<Props> = ({ rmaDetails, currencySymbol }) => {
    if (!rmaDetails || !rmaDetails.rMAHistoryLines || rmaDetails.rMAHistoryLines.length === 0) {
        return null;
    }

    const isShareEntity = getIsShareEntity();

    return (
        <GridContainer {...styles.gridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText}>{translate("RMA Summary")}</Typography>
            </GridItem>

            {rmaDetails.rMAHistoryLines.map((rmaLines, index: number) => (
                <StyledWrapper {...styles.productContainer} key={rmaLines.itemId}>
                    <GridItem {...styles.headerContainer}>
                        <OrderStatusDisplay status={rmaDetails.statusDisplay || ""}>
                            {rmaDetails.statusDisplay || ""}
                        </OrderStatusDisplay>
                    </GridItem>
                    <GridContainer>
                        <GridItem {...styles.imageContainer}>
                            <LazyImage
                                src={
                                    rmaLines?.mediumImagePath ||
                                    "https://dlf981snf1i91.cloudfront.net/userfiles/images/products/md_notfound.png"
                                }
                                isShareEntity={isShareEntity}
                            />
                        </GridItem>
                        <GridItem {...styles.contentContainer}>
                            <GridItem {...styles.content}>
                                <Typography {...styles.brandTitle}>{rmaLines.brandName || ""}</Typography>
                                {rmaLines.productUri ? (
                                    <Link href={rmaLines.productUri}>
                                        <Typography {...styles.productTitle}>{rmaLines.productTitle}</Typography>
                                    </Link>
                                ) : (
                                    <Typography {...styles.productTitle}>{rmaLines.productTitle || ""}</Typography>
                                )}
                            </GridItem>

                            <GridItem {...styles.content}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Unit Price")}
                                    text={``}
                                />
                                <Typography
                                    {...styles.unitPriceContent}
                                >{`${rmaLines?.priceDisplay || ""}`}</Typography>
                            </GridItem>

                            <GridItem {...styles.content}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("QTY Requested")}
                                    text={`${rmaLines?.qtyRequested}`}
                                />
                            </GridItem>

                            <GridItem {...styles.content}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("QTY Authorized")}
                                    text={`${rmaLines?.qtyAuthorized}`}
                                />
                            </GridItem>

                            <GridItem {...styles.content}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Customer Part #")}
                                    text={rmaLines?.partNumber}
                                />
                            </GridItem>

                            <GridItem {...styles.content}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Supplier Part #")}
                                    text={rmaLines?.itemId || ""}
                                />
                            </GridItem>
                            <GridItem {...styles.content}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Qty Returned")}
                                    text={`${rmaLines?.qtyReturned}`}
                                />
                            </GridItem>

                            <GridItem {...styles.content}>
                                <SmallHeadingAndText
                                    extendedStyles={styles.headingAndTextStyles}
                                    heading={translate("Qty Credited")}
                                    text={`${rmaLines?.qtyCredited}`}
                                />
                            </GridItem>
                        </GridItem>
                    </GridContainer>

                    <GridItem width={12} {...styles.footerPoWrapper}>
                        <GridContainer>
                            <GridItem width={[9, 9, 5, 2, 2]} {...styles.poLineContainer}>
                                <SmallHeadingAndContent
                                    heading={translate("Po Line Number")}
                                    text={
                                        <StyledWrapper {...styles.poLineNumberParent}>
                                            <StyledWrapper {...styles.poLineNumber}>
                                                {rmaLines?.poLineNumber || ""}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    }
                                    extendedStyles={styles.trackOrderHeadingAndText}
                                />
                            </GridItem>

                            <GridItem width={[6, 6, 5, 6, 6]}></GridItem>

                            <GridItem width={[3, 3, 2, 4, 4]} {...styles.lineNumberParentGrid}>
                                <StyledWrapper {...styles.lineNumberParent}>
                                    <StyledWrapper {...styles.lineNumber}>{index + 1}</StyledWrapper>
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </StyledWrapper>
            ))}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RMASummary),
    definition: {
        group: "Common",
        displayName: "RMASummary",
        fieldDefinitions: [],
    },
};

export default widgetModule;
